"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import { useContentTab } from "@/components/brain/ContentTabContext";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import MyStreamWaveTabsMemeSubmit from "./MyStreamWaveTabsMemeSubmit";
import { useWave } from "../../../../hooks/useWave";
import { useDecisionPoints } from "../../../../hooks/waves/useDecisionPoints";
import { Time } from "../../../../helpers/time";
import type { TimeLeft } from "../../../../helpers/waves/time.utils";
import { calculateTimeLeft } from "../../../../helpers/waves/time.utils";
import { CompactTimeCountdown } from "../../../waves/leaderboard/time/CompactTimeCountdown";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleLeftIcon, ArrowLeftIcon, MagnifyingGlassIcon, ShareIcon, LinkIcon, CheckIcon } from "@heroicons/react/24/outline";
import WavePicture from "../../../waves/WavePicture";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { createBreakpoint } from "react-use";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
import WaveDescriptionPopover from "@/components/waves/header/WaveDescriptionPopover";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
import MyStreamActionTooltip from "../MyStreamActionTooltip";
import MyStreamWaveCreateCurationAction from "./MyStreamWaveCreateCurationAction";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  MD: 768,
  S: 0
});
const EMPTY_TIME_LEFT: TimeLeft = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0
};
interface MyStreamWaveTabsMemeProps {
  readonly wave: ApiWave;
  readonly activeCurationId: string | null;
  readonly onSelectCuration: (curationId: string | null) => void;
}
const MyStreamWaveTabsMeme: React.FC<MyStreamWaveTabsMemeProps> = t0 => {
  const $ = _c(117);
  const {
    wave,
    activeCurationId,
    onSelectCuration
  } = t0;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const [isMemesModalOpen, setIsMemesModalOpen] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const breakpoint = useBreakpoint();
  const isCompact = breakpoint === "S";
  const showExternalCreateCurationAction = breakpoint === "LG";
  const showBackButton = breakpoint !== "LG";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const showShareAction = !isDirectMessage;
  let t1;
  if ($[0] !== wave) {
    t1 = getWaveDescriptionPreviewText(wave);
    $[0] = wave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewText = t1;
  const showDescriptionPreview = showShareAction && !!previewText;
  let t2;
  if ($[2] !== isDirectMessage || $[3] !== wave.id || $[4] !== wave.name) {
    t2 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[2] = isDirectMessage;
    $[3] = wave.id;
    $[4] = wave.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t2);
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  const headerActionsTooltipId = `my-stream-wave-meme-header-actions-${wave.id}`;
  const rightSidebarActionLabel = isRightSidebarOpen ? "Hide right sidebar" : "Show right sidebar";
  let t3;
  if ($[6] !== waveLinkActionFeedbackState || $[7] !== waveLinkActionMode) {
    t3 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[6] = waveLinkActionFeedbackState;
    $[7] = waveLinkActionMode;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const renderWaveLinkActionIcon = t3;
  const {
    isMemesWave,
    isRankWave,
    pauses: t4
  } = useWave(wave);
  const {
    filterDecisionsDuringPauses
  } = t4;
  const {
    allDecisions
  } = useDecisionPoints(wave);
  let t5;
  if ($[9] !== allDecisions || $[10] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t5 = allDecisions.filter(decision_0 => filtered.some(f => f.decision_time === decision_0.timestamp));
    $[9] = allDecisions;
    $[10] = filterDecisionsDuringPauses;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const filteredDecisions = t5;
  let t6;
  if ($[12] !== filteredDecisions) {
    t6 = filteredDecisions.find(_temp2)?.timestamp ?? null;
    $[12] = filteredDecisions;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const nextDecisionTime = t6;
  const [timeLeft, setTimeLeft] = useState(EMPTY_TIME_LEFT);
  let t7;
  let t8;
  if ($[14] !== nextDecisionTime) {
    t7 = () => {
      if (typeof nextDecisionTime !== "number") {
        return;
      }
      const intervalId = setInterval(() => {
        const newTimeLeft = calculateTimeLeft(nextDecisionTime);
        setTimeLeft(newTimeLeft);
        if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
          clearInterval(intervalId);
        }
      }, 1000);
      return () => clearInterval(intervalId);
    };
    t8 = [nextDecisionTime];
    $[14] = nextDecisionTime;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  const displayedTimeLeft = typeof nextDecisionTime === "number" ? timeLeft : EMPTY_TIME_LEFT;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsMemesModalOpen(true);
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleMemesSubmit = t9;
  let t10;
  if ($[18] !== isApp || $[19] !== router || $[20] !== searchParams || $[21] !== wave.chat.scope.group?.is_direct_message) {
    t10 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      params.delete("curation");
      const basePath = getWaveHomeRoute({
        isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[18] = isApp;
    $[19] = router;
    $[20] = searchParams;
    $[21] = wave.chat.scope.group?.is_direct_message;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const handleMobileBack = t10;
  let t11;
  if ($[23] !== onSelectCuration || $[24] !== pathname || $[25] !== router || $[26] !== searchParams || $[27] !== setActiveContentTab || $[28] !== wave.id || $[29] !== waveChatScroll) {
    t11 = serialNo => {
      onSelectCuration(null);
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.delete("curation");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[23] = onSelectCuration;
    $[24] = pathname;
    $[25] = router;
    $[26] = searchParams;
    $[27] = setActiveContentTab;
    $[28] = wave.id;
    $[29] = waveChatScroll;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleSearchSelect = t11;
  let t12;
  if ($[31] !== handleMobileBack || $[32] !== showBackButton) {
    t12 = showBackButton && <button onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[31] = handleMobileBack;
    $[32] = showBackButton;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = wave.name;
  const t14 = wave.picture;
  let t15;
  if ($[34] !== wave.contributors_overview) {
    t15 = wave.contributors_overview.map(_temp3);
    $[34] = wave.contributors_overview;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== wave.name || $[38] !== wave.picture) {
    t16 = <div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={t13} picture={t14} contributors={t15} /></div>;
    $[36] = t15;
    $[37] = wave.name;
    $[38] = wave.picture;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== previewText || $[41] !== showDescriptionPreview || $[42] !== wave) {
    t17 = showDescriptionPreview ? <WaveDescriptionPopover wave={wave} align="left" ariaLabel="Show wave description" triggerClassName="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><h1 className="tw-mb-0 tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1><span className="tw-mt-0.5 tw-block tw-w-full tw-truncate tw-text-xs tw-font-normal tw-text-iron-400">{previewText}</span></WaveDescriptionPopover> : <h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>;
    $[40] = previewText;
    $[41] = showDescriptionPreview;
    $[42] = wave;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t12 || $[45] !== t16 || $[46] !== t17) {
    t18 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-3">{t12}{t16}{t17}</div>;
    $[44] = t12;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== isCompact || $[49] !== wave) {
    t19 = !isCompact && <MyStreamWaveTabsMemeSubmit handleMemesSubmit={handleMemesSubmit} wave={wave} />;
    $[48] = isCompact;
    $[49] = wave;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== handleWaveLinkActionClick || $[52] !== headerActionsTooltipId || $[53] !== renderWaveLinkActionIcon || $[54] !== showShareAction || $[55] !== waveLinkActionIconColor || $[56] !== waveLinkActionLabel || $[57] !== waveLinkActionMode) {
    t20 = showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[51] = handleWaveLinkActionClick;
    $[52] = headerActionsTooltipId;
    $[53] = renderWaveLinkActionIcon;
    $[54] = showShareAction;
    $[55] = waveLinkActionIconColor;
    $[56] = waveLinkActionLabel;
    $[57] = waveLinkActionMode;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setIsSearchOpen(true);
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== headerActionsTooltipId) {
    t23 = <button type="button" onClick={t21} aria-label="Search messages in this wave" data-tooltip-id={headerActionsTooltipId} data-tooltip-content="Search messages in this wave" className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{t22}</button>;
    $[61] = headerActionsTooltipId;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  const t24 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t25;
  if ($[63] !== t24) {
    t25 = <ChevronDoubleLeftIcon strokeWidth={2} className={t24} />;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== headerActionsTooltipId || $[66] !== rightSidebarActionLabel || $[67] !== t25 || $[68] !== toggleRightSidebar) {
    t26 = <button type="button" onClick={toggleRightSidebar} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={rightSidebarActionLabel} className="tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-700 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]" aria-label={rightSidebarActionLabel}>{t25}</button>;
    $[65] = headerActionsTooltipId;
    $[66] = rightSidebarActionLabel;
    $[67] = t25;
    $[68] = toggleRightSidebar;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== t19 || $[71] !== t20 || $[72] !== t23 || $[73] !== t26) {
    t27 = <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2 md:tw-shrink-0">{t19}{t20}{t23}{t26}</div>;
    $[70] = t19;
    $[71] = t20;
    $[72] = t23;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== t18 || $[76] !== t27) {
    t28 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-overflow-x-hidden tw-px-2 tw-py-3 sm:tw-px-4">{t18}{t27}</div>;
    $[75] = t18;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== headerActionsTooltipId) {
    t29 = <MyStreamActionTooltip id={headerActionsTooltipId} />;
    $[78] = headerActionsTooltipId;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  const t30 = !showExternalCreateCurationAction;
  let t31;
  if ($[80] !== activeContentTab || $[81] !== activeCurationId || $[82] !== onSelectCuration || $[83] !== setActiveContentTab || $[84] !== t30 || $[85] !== wave) {
    t31 = <div className="tw-min-w-0 tw-flex-1"><MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} showCreateCurationAction={t30} /></div>;
    $[80] = activeContentTab;
    $[81] = activeCurationId;
    $[82] = onSelectCuration;
    $[83] = setActiveContentTab;
    $[84] = t30;
    $[85] = wave;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== onSelectCuration || $[88] !== showExternalCreateCurationAction || $[89] !== wave) {
    t32 = showExternalCreateCurationAction && <MyStreamWaveCreateCurationAction wave={wave} onCreated={onSelectCuration} />;
    $[87] = onSelectCuration;
    $[88] = showExternalCreateCurationAction;
    $[89] = wave;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== displayedTimeLeft || $[92] !== isMemesWave || $[93] !== isRankWave || $[94] !== nextDecisionTime) {
    t33 = (isMemesWave || isRankWave) && typeof nextDecisionTime === "number" && <div className="tw-hidden md:tw-flex md:tw-flex-shrink-0 md:tw-pr-4"><CompactTimeCountdown timeLeft={displayedTimeLeft} /></div>;
    $[91] = displayedTimeLeft;
    $[92] = isMemesWave;
    $[93] = isRankWave;
    $[94] = nextDecisionTime;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  let t34;
  if ($[96] !== t31 || $[97] !== t32 || $[98] !== t33) {
    t34 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-x-0 tw-border-y tw-border-solid tw-border-iron-800">{t31}{t32}{t33}</div>;
    $[96] = t31;
    $[97] = t32;
    $[98] = t33;
    $[99] = t34;
  } else {
    t34 = $[99];
  }
  let t35;
  if ($[100] !== t28 || $[101] !== t29 || $[102] !== t34) {
    t35 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t28}{t29}{t34}</div>;
    $[100] = t28;
    $[101] = t29;
    $[102] = t34;
    $[103] = t35;
  } else {
    t35 = $[103];
  }
  let t36;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = () => setIsMemesModalOpen(false);
    $[104] = t36;
  } else {
    t36 = $[104];
  }
  let t37;
  if ($[105] !== isMemesModalOpen || $[106] !== wave) {
    t37 = <MemesArtSubmissionModal isOpen={isMemesModalOpen} wave={wave} onClose={t36} />;
    $[105] = isMemesModalOpen;
    $[106] = wave;
    $[107] = t37;
  } else {
    t37 = $[107];
  }
  let t38;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = () => setIsSearchOpen(false);
    $[108] = t38;
  } else {
    t38 = $[108];
  }
  let t39;
  if ($[109] !== handleSearchSelect || $[110] !== isSearchOpen || $[111] !== wave) {
    t39 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t38} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[109] = handleSearchSelect;
    $[110] = isSearchOpen;
    $[111] = wave;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  let t40;
  if ($[113] !== t35 || $[114] !== t37 || $[115] !== t39) {
    t40 = <>{t35}{t37}{t39}</>;
    $[113] = t35;
    $[114] = t37;
    $[115] = t39;
    $[116] = t40;
  } else {
    t40 = $[116];
  }
  return t40;
};
export default MyStreamWaveTabsMeme;
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp3(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}